import { createRootRoute, Outlet } from "@tanstack/react-router";
import { I18nextProvider } from "react-i18next";
import { CssBaseline } from "@mui/material";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { useIntercom } from "react-use-intercom";
import type { PropsWithChildren } from "react";
import { ErrorBoundary } from "@sentry/react";
import { i18next } from "@/lib/i18n";
import AppProviders from "@/providers";
import { PaymentMethod, useAuth } from "@/features/auth";
import { useClarityTracking } from "@/hooks";

const IntercomInitializer = ({ children }: PropsWithChildren) => {
  const { user, isInitialized } = useAuth();
  const { boot, update } = useIntercom();
  const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;

  if (!isInitialized) return null;

  if (user) {
    const isFreePlanUser = user.payment_method === PaymentMethod.Free;

    const payload = {
      app_id: INTERCOM_APP_ID,
      email: user.email,
    };

    boot(payload);
    update({ hideDefaultLauncher: isFreePlanUser });
  }

  return children;
};

const RootComponent = () => {
  useClarityTracking();

  return (
    <>
      <CssBaseline />
      <I18nextProvider i18n={i18next}>
        <AppProviders>
          <IntercomInitializer>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </IntercomInitializer>
        </AppProviders>
      </I18nextProvider>
      <TanStackRouterDevtools />
    </>
  );
};

const Route = createRootRoute({
  component: RootComponent,
});

export { Route };
